import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect } from "react";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import Loading from "@/components/Loading";
import NoData from "@/components/NoData";
import BetOKMatchLeagues from "../BetOKMatchLeagues";
import BetOKMatch from "../BetOKMatch";
import BetOKDateTabs from "../BetOKDateTabs";
import LoadMoreButton from "../LoadMoreButton";
import LeagueFilterV10 from "../LeagueFilterV10";
import { BANG_KEO_PAGE_LIMIT, BANKER_EXTENDS } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvTiLeV10 = ({ type = "all" }) => {

  const { colorPrimary, colorSecondary, colorBg, colorText, colorForeground, colorTableHeaderStart, colorTableHeaderEnd, hasDataConfig } = useCompConfig();
  const themeStyle = hasDataConfig
    ? {
      "--theme-primary": colorPrimary,
      "--theme-highlight": colorSecondary,
      "--theme-bg": colorBg,
      "--theme-text": colorText,
      "--theme-foreground": colorForeground,
      ...(colorTableHeaderStart != null && { "--theme-table-header-bg-start": colorTableHeaderStart }),
      ...(colorTableHeaderEnd != null && { "--theme-table-header-bg-end": colorTableHeaderEnd }),
    }
    : undefined;

  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedDate, setSelectedDate] = useState("live");
  const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(20);

  const today = dayjs().tz(TZ);
  const todayString = today.format("YYYY-MM-DD");

  const dateString =
    !selectedDate || selectedDate === ""
      ? "all"
      : selectedDate === "live"
        ? todayString
        : selectedDate;
  const status =
    type === "ketqua" ? "end" : selectedDate === "live" ? "live" : "";

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, status],
  );

  const {
    leaguesWithMatches,
    loading,
    error,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const baseLeaguesWithMatches = useMemo(
    () =>
      (leaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [leaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(() => {
    if (!selectedLeagueIds?.length) return baseLeaguesWithMatches;
    // Stringify IDs for type-safe comparison
    const idSet = new Set(selectedLeagueIds.map(String));
    return baseLeaguesWithMatches.filter(({ league }) =>
      idSet.has(String(league?.id)),
    );
  }, [baseLeaguesWithMatches, selectedLeagueIds]);

  const allFilteredMatches = useMemo(() => {
    return filteredLeaguesWithMatches.flatMap(({ league, matches }) =>
      (matches || []).map((m) => ({ ...m, league })),
    );
  }, [filteredLeaguesWithMatches]);

  const visibleMatches = useMemo(() => {
    return allFilteredMatches.slice(0, displayLimit);
  }, [allFilteredMatches, displayLimit]);

  const groupedVisibleMatches = useMemo(() => {
    const groups = [];
    visibleMatches.forEach((match) => {
      const leagueId = String(match.league?.id);
      let group = groups.find((g) => String(g.league?.id) === leagueId);
      if (!group) {
        group = { league: match.league, matches: [] };
        groups.push(group);
      }
      group.matches.push(match);
    });
    return groups;
  }, [visibleMatches]);

  useEffect(() => {
    // Auto-fetch if filtered results are below display limit
    if (
      allFilteredMatches.length < displayLimit + 1 &&
      hasMore &&
      !loading &&
      !isLoadingMore
    ) {
      handleLoadMore();
    }
  }, [
    allFilteredMatches.length,
    displayLimit,
    hasMore,
    loading,
    isLoadingMore,
    handleLoadMore,
  ]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();

  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  useEffect(() => {
    if (
      firstLoad &&
      selectedDate === "live" &&
      !loading &&
      filteredLeaguesWithMatches.length <= 0
    ) {
      setSelectedDate(todayString);
    }
  }, [
    firstLoad,
    selectedDate,
    loading,
    filteredLeaguesWithMatches,
    todayString,
  ]);

  const handleLeagueFilterChange = (ids) => {
    setSelectedLeagueIds(ids);
    setDisplayLimit(20); // Reset display limit
  };

  const handleDateChange = (value) => {
    setSelectedDate(value);
    setFirstLoad(false);
    setDisplayLimit(20); // Reset display limit
  };

  return (
    <div
      className="mx-auto flex min-h-screen flex-col gap-3 rounded-xl px-2 md:px-2 py-3 bg-[var(--kbv-bg-page)]"
      style={themeStyle}
    >
      <div className="relative overflow-x-auto overflow-y-hidden mr-0.5 mb-4 box-border w-full rounded-xl border border-[var(--kbv-card-border)] bg-[var(--kbv-card-bg)] max-md:mb-0 max-md:min-h-[49px] max-md:rounded-t-[14px] max-md:rounded-b-none max-md:shadow-none max-md:px-0 md:max-[1056px]:min-h-[56px] md:max-[1056px]:px-5 min-[1057px]:min-h-16">
        <div className="flex h-full w-full min-w-0 gap-2 flex-col md:flex-row md:items-center md:gap-x-3 py-2 md:py-0">
          <div className="flex h-full min-w-0 flex-1 max-md:flex-none max-md:min-h-0 md:flex-[0_0_75%] md:min-w-0 md:overflow-hidden items-center max-md:mt-0 mt-[4px]">
            <BetOKDateTabs
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              compact
            />
          </div>
          <div className="flex shrink-0 items-center justify-center md:justify-end w-full md:flex-[0_0_22%] md:min-w-0">
            <LeagueFilterV10
              selectedIds={selectedLeagueIds}
              onChange={handleLeagueFilterChange}
              className="h-full w-full"
              themeStyle={themeStyle}
            />
          </div>
        </div>
      </div>

      {loading && <Loading />}
      {!loading && error && <NoData message="Lỗi tải dữ liệu trận đấu" />}
      {!loading && (
        <>
          <div className="rounded-lg">
            {groupedVisibleMatches.length === 0 ? (
              <NoData
                message={
                  isLoadingMore
                    ? "Đang tìm thêm trận đấu phù hợp..."
                    : "Không tìm thấy trận đấu theo tiêu chí lọc"
                }
              />
            ) : (
              groupedVisibleMatches.map(({ league, matches }, leagueIndex) => (
                <BetOKMatchLeagues
                  key={league?.id || `league-${leagueIndex}`}
                  league={league || {}}
                  matches={matches}
                  render={(match, idx) => (
                    match ? <BetOKMatch key={match.id || `${league?.id || leagueIndex}-${match?.matchTime || Math.random()}`} match={match} index={idx} /> : null
                  )}
                />
              ))
            )}
          </div>
          <LoadMoreButton
            isLoading={isLoadingMore}
            onClick={() => setDisplayLimit((prev) => prev + 20)}
            loadingComponent={<Loading />}
            hasMore={allFilteredMatches.length > displayLimit || hasMore}
          />
        </>
      )}
    </div>
  );
};

export default KbvTiLeV10;
